/**
 * Il registro delle operazioni: tutto quello che è successo a un evento.
 *
 * `recentLogs` (`EventStateDto`) tiene le **ultime 25** operazioni ed è la
 * colonna laterale della schermata di lavoro: chi sta al banco vuole vedere cosa
 * ha appena fatto, non la giornata. Il registro è l'altra cosa — la ricostruzione
 * completa, dalla più recente, di restituzioni, annullamenti, rimozioni e
 * ripristini, con l'anagrafica di chi ha restituito.
 *
 * Una riga di `return_logs` è **immutabile**: si scrive e non si corregge mai.
 * È la proprietà che rende sicuro appendere dal socket e rileggere per intero
 * alla riconnessione senza dover decidere chi vince.
 */
import type { ChipDto, ReturnAction, ReturnLogDto } from './dto';

/**
 * L'anagrafica di un chip come entra nel registro.
 *
 * Volutamente **non** un `ChipDto`: al registro servono il nome da leggere ad
 * alta voce e i riferimenti per ritrovare la persona, non lo stato di rientro
 * (che è già raccontato dall'operazione, ed è quello di *allora*). Chiedere meno
 * campi rende il mapper del server e l'append dal socket facili da tenere
 * allineati.
 */
export type ChipIdentity = {
  firstName: string | null;
  lastName: string | null;
  team: string | null;
  category: string | null;
  raceNumber: string | null;
};

/**
 * Una riga del registro: l'operazione, più l'anagrafica di chi la riguarda.
 *
 * Il `code` è quello del log, non quello del chip: è il codice com'era **nel
 * momento dell'operazione**. Se un domani un codice venisse corretto, il
 * registro deve continuare a raccontare cosa è stato letto allora.
 */
export type OperationLogEntryDto = ReturnLogDto & {
  firstName: string | null;
  lastName: string | null;
  team: string | null;
  category: string | null;
  raceNumber: string | null;
};

export type EventOperationsDto = {
  /** Tutte le operazioni dell'evento, dalla più recente. */
  operations: OperationLogEntryDto[];
};

/** Come si legge ogni operazione sullo schermo. */
export const RETURN_ACTION_LABELS: Record<ReturnAction, string> = {
  return: 'Restituito',
  unreturn: 'Annullato',
  remove: 'Rimosso',
  restore: 'Ripristinato',
};

/**
 * L'unica definizione di «operazione + anagrafica».
 *
 * La usano **sia** il mapper del server **sia** l'append dal socket: se le due
 * la costruissero per conto proprio, la stessa operazione arriverebbe diversa a
 * seconda della strada — REST o realtime — e la differenza si vedrebbe solo
 * ricaricando la pagina. È la stessa ragione per cui i mapper del server sono in
 * un posto solo.
 */
export function operationLogEntry(log: ReturnLogDto, chip: ChipIdentity): OperationLogEntryDto {
  return {
    ...log,
    firstName: chip.firstName,
    lastName: chip.lastName,
    team: chip.team,
    category: chip.category,
    raceNumber: chip.raceNumber,
  };
}

/** L'anagrafica di un chip, nella forma che serve a `operationLogEntry`. */
export function chipIdentity(chip: ChipDto): ChipIdentity {
  return {
    firstName: chip.firstName,
    lastName: chip.lastName,
    team: chip.team,
    category: chip.category,
    raceNumber: chip.raceNumber,
  };
}

/**
 * Unisce il registro che abbiamo con quello appena letto o appena arrivato.
 *
 * Serve perché le due strade — la rilettura REST e gli annunci del socket —
 * possono sovrapporsi: una risposta di rete può essere nata **prima** di
 * un'operazione che il socket ci ha già annunciato, e in quel caso il registro
 * letto è più vecchio di quello che abbiamo in mano. Si unisce per
 * identificativo e si riordina, così nessuna operazione si perde e nessuna
 * compare due volte.
 *
 * **Nessun taglio**: è la differenza da `mergeLogs`, che invece tronca a 25
 * perché alimenta la colonna laterale. Questo è il registro, e la richiesta era
 * «tutte le operazioni»: un troncamento silenzioso qui sarebbe un bug che si
 * scopre solo quando serve la riga che manca.
 *
 * L'ordine — ora decrescente, e a pari ora l'identificativo decrescente — è
 * **lo stesso della query del server** (`orderBy(desc(at), desc(id))`), così la
 * pagina non si riordina sotto gli occhi dopo la prima rilettura.
 */
export function mergeOperationLogs(
  current: readonly OperationLogEntryDto[],
  incoming: readonly OperationLogEntryDto[],
): OperationLogEntryDto[] {
  const byId = new Map<string, OperationLogEntryDto>();
  for (const entry of [...incoming, ...current]) {
    if (!byId.has(entry.id)) byId.set(entry.id, entry);
  }

  return [...byId.values()].sort(compareOperations);
}

function compareOperations(a: OperationLogEntryDto, b: OperationLogEntryDto): number {
  const byTime = toTime(b.at) - toTime(a.at);
  if (byTime !== 0) return byTime;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

function toTime(value: string): number {
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}
